import React, { useEffect, useRef } from 'react'

// A strip of cards that drifts left on a loop. The cards are rendered twice
// so the loop is seamless. Hovering pauses the drift; the arrows glide it
// one card forward or back.

const SPEED = 55 // px per second

const HighlightsRail = ({ items, header }) => {
  const railRef = useRef(null)
  const trackRef = useRef(null)
  const state = useRef({ offset: 0, nudge: 0, hovered: false, visible: true })

  useEffect(() => {
    const rail = railRef.current
    const track = trackRef.current
    if (!rail || !track) return
    const s = state.current
    const autoplay = !window.matchMedia('(prefers-reduced-motion: reduce)')
      .matches
    let raf = 0
    let prev = 0

    const loopLength = () => {
      const cards = track.children
      return cards[items.length].offsetLeft - cards[0].offsetLeft
    }

    const frame = (now) => {
      const dt = Math.min((now - (prev || now)) / 1000, 0.05)
      prev = now
      const loop = loopLength()

      if (autoplay && !s.hovered && s.visible) s.offset += SPEED * dt
      // Ease any pending arrow nudge in over a few frames.
      const step = s.nudge * Math.min(dt * 7, 1)
      s.offset += step
      s.nudge -= step
      if (Math.abs(s.nudge) < 0.5) {
        s.offset += s.nudge
        s.nudge = 0
      }

      s.offset = ((s.offset % loop) + loop) % loop
      track.style.transform = `translate3d(${-s.offset}px, 0, 0)`
      raf = requestAnimationFrame(frame)
    }

    const io = new IntersectionObserver(([entry]) => {
      s.visible = entry.isIntersecting
    })
    io.observe(rail)
    raf = requestAnimationFrame(frame)
    return () => {
      cancelAnimationFrame(raf)
      io.disconnect()
    }
  }, [items.length])

  const go = (dir) => {
    const cards = trackRef.current.children
    const stride = cards[1].offsetLeft - cards[0].offsetLeft
    state.current.nudge += dir * stride
  }

  const controls = (
    <div className='rail-controls'>
      <button onClick={() => go(-1)} aria-label='Previous highlight'>
        <i className='fa-solid fa-arrow-left' />
      </button>
      <button onClick={() => go(1)} aria-label='Next highlight'>
        <i className='fa-solid fa-arrow-right' />
      </button>
    </div>
  )

  return (
    <>
      {header(controls)}
      <div
        className='rail'
        ref={railRef}
        onPointerEnter={() => (state.current.hovered = true)}
        onPointerLeave={() => (state.current.hovered = false)}
      >
        <div className='rail__track' ref={trackRef}>
          {[...items, ...items].map((h, i) => (
            <figure
              className='rail-card'
              key={i}
              aria-hidden={i >= items.length ? 'true' : undefined}
            >
              <div className='rail-card__media'>
                <img src={h.img} alt='' />
                <span className='rail-card__badge'>{h.badge}</span>
              </div>
              <figcaption>
                {h.text}
                {h.link && ' '}
                {h.link && (
                  <a
                    className='rail-card__link'
                    href={h.link}
                    target='_blank'
                    rel='noopener noreferrer'
                    tabIndex={i >= items.length ? -1 : undefined}
                  >
                    Read the announcement{' '}
                    <i className='fa-solid fa-arrow-up-right-from-square' />
                  </a>
                )}
              </figcaption>
            </figure>
          ))}
        </div>
      </div>
    </>
  )
}

export default HighlightsRail
